import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { logAdminOperation } from '../../lib/adminActivityLogger';
import { PlusCircle, Trash2, Upload } from 'lucide-react';

// Reusable component for a form section
const SectionEditor = ({ title, children }) => (
    <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h2 className="text-xl font-semibold mb-4 border-b pb-2">{title}</h2>
        {children}
    </div>
);

// Reusable input field component
const InputField = ({ id, name, label, value, onChange, placeholder = '', type = 'text', min, max, step }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <input
            type={type}
            id={id}
            name={name}
            value={value !== undefined && value !== null ? value : ''}
            onChange={onChange}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
        />
    </div>
);

// Reusable select field component
const SelectField = ({ id, name, label, value, onChange, options }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <select
            id={id}
            name={name}
            value={value || ''}
            onChange={onChange}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
        >
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    </div>
);

// Reusable color picker component
const ColorPickerInput = ({ id, name, label, value, onChange, defaultValue = '#ffffff' }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="flex items-center gap-2">
            <input
                type="color"
                id={`${id}-picker`}
                name={name}
                value={value || defaultValue}
                onChange={onChange}
                className="h-10 w-10 rounded border border-gray-300 cursor-pointer p-0.5 flex-shrink-0"
            />
            <input
                type="text"
                id={id}
                name={name}
                value={value || ''}
                onChange={onChange}
                placeholder={defaultValue}
                className="flex-1 p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            />
        </div>
    </div>
);

const alignOptions = [
    { value: 'center', label: '中央寄せ' },
    { value: 'left', label: '左寄せ' },
    { value: 'right', label: '右寄せ' },
];

// Component for managing a list of items (like features or flow steps)
const DynamicListEditor = ({ title, items, onUpdate, onAdd, onRemove, titleLabel = "タイトル", descriptionLabel = "説明", showIconField = false }) => (
    <div>
        <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">{title}</h3>
            <button onClick={onAdd} className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                <PlusCircle size={16} className="mr-1" /> 追加
            </button>
        </div>
        <div className="space-y-4">
            {items?.map((item, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border relative grid grid-cols-1 gap-4">
                    <button onClick={() => onRemove(index)} className="absolute top-2 right-2 text-gray-400 hover:text-red-600">
                        <Trash2 size={16} />
                    </button>
                    
                    {showIconField && (
                         <InputField id={`icon-${index}`} name="icon" label="アイコン名" value={item.icon} onChange={e => onUpdate(e, index)} placeholder="例: Zap" />
                    )}

                    <InputField id={`title-${index}`} name="title" label={titleLabel} value={item.title} onChange={e => onUpdate(e, index)} />
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{descriptionLabel}</label>
                        <textarea name="description" rows={3} value={item.description} onChange={e => onUpdate(e, index)} className="w-full p-2 border border-gray-300 rounded-md" />
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const LpManagement = () => {
  const [formData, setFormData] = useState({ hero: {}, cta: {}, features: { problems: [], solutions: [] }, flow: { steps: [] }, faq: { items: [] } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);

  const docRef = useCallback(() => doc(db, 'lp_contents', 'main'), []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const docSnap = await getDoc(docRef());
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFormData({
            hero: data.hero || {},
            features: data.features || { problems: [], solutions: [] },
            flow: data.flow || { steps: [] },
            faq: data.faq || { items: [] },
            cta: data.cta || {},
          });
        } else {
          setFormData({ hero: {}, cta: {}, features: { problems: [], solutions: [] }, flow: { steps: [] }, faq: { items: [] } });
        }
      } catch (err) {
        setError('データの読み込みに失敗しました。');
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [docRef]);

  const handleSimpleChange = (e, section) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [section]: { ...prev[section], [name]: value } }));
  };

  const handleDynamicListChange = (e, section, listName, index) => {
    const { name, value } = e.target;
    const updatedItems = [...formData[section][listName]];
    updatedItems[index] = { ...updatedItems[index], [name]: value };
    setFormData(prev => ({ ...prev, [section]: { ...prev[section], [listName]: updatedItems } }));
  };

 const addDynamicListItem = (section, listName, hasIcon = false) => {
    const newItem = { title: '', description: '' };
    if (hasIcon) {
        newItem.icon = '';
    }
    setFormData(prev => ({
        ...prev,
        [section]: {
            ...prev[section],
            [listName]: [...(prev[section]?.[listName] || []), newItem]
        }
    }));
 };

  const removeDynamicListItem = (section, listName, index) => {
    const updatedItems = formData[section][listName].filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, [section]: { ...prev[section], [listName]: updatedItems } }));
  };

  const handleImageUpload = async (e, section, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const storageRef = ref(storage, `lp-images/${section}-${field}-${Date.now()}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      setFormData(prev => ({
          ...prev,
          [section]: { ...prev[section], [field]: downloadURL }
      }));

    } catch (err) {
      console.error("Image upload error:", err);
      setError('画像のアップロードに失敗しました。');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess(false);
      setError(null);
      await setDoc(docRef(), formData, { merge: true });
      
      // Record Admin Operation Log: lp_content_update
      await logAdminOperation({
        operation_type: 'lp_content_update',
        target_type: 'lp_content',
        target_id: 'main',
        target_name: 'LPコンテンツ',
        reason: 'LPコンテンツ更新'
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Firestore save error:", err);
      setError('データの保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>読み込み中...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">LPコンテンツ管理</h1>
        <button onClick={handleSave} disabled={saving || uploading} className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 disabled:bg-gray-400">
          {saving ? '保存中...' : (uploading ? 'アップロード中...' : '保存')}
        </button>
      </div>

      {success && <div className="bg-green-100 text-green-700 p-3 rounded-lg mb-4">正常に保存されました。</div>}
      {error && <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4">エラー: {error}</div>}

      <SectionEditor title="ヒーローセクション">
        {/* メインタイトル */}
        <div className="border-b pb-6 mb-6">
          <h3 className="text-md font-semibold text-gray-800 mb-3">メインタイトル</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-3">
              <InputField id="title" name="title" label="メインタイトルテキスト" value={formData.hero?.title} onChange={e => handleSimpleChange(e, 'hero')} />
            </div>
            <SelectField
              id="titleAlign"
              name="titleAlign"
              label="配置（位置）"
              value={formData.hero?.titleAlign || 'center'}
              onChange={e => handleSimpleChange(e, 'hero')}
              options={alignOptions}
            />
            <ColorPickerInput
              id="titleColor"
              name="titleColor"
              label="文字色"
              value={formData.hero?.titleColor}
              onChange={e => handleSimpleChange(e, 'hero')}
              defaultValue="#ffffff"
            />
          </div>
        </div>

        {/* サブタイトル */}
        <div className="border-b pb-6 mb-6">
          <h3 className="text-md font-semibold text-gray-800 mb-3">サブタイトル</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-3">
              <InputField id="subtitle" name="subtitle" label="サブタイトルテキスト" value={formData.hero?.subtitle} onChange={e => handleSimpleChange(e, 'hero')} />
            </div>
            <SelectField
              id="subtitleAlign"
              name="subtitleAlign"
              label="配置（位置）"
              value={formData.hero?.subtitleAlign || 'center'}
              onChange={e => handleSimpleChange(e, 'hero')}
              options={alignOptions}
            />
            <ColorPickerInput
              id="subtitleColor"
              name="subtitleColor"
              label="文字色"
              value={formData.hero?.subtitleColor}
              onChange={e => handleSimpleChange(e, 'hero')}
              defaultValue="#ffffff"
            />
          </div>
        </div>

        {/* CTAボタン */}
        <div className="border-b pb-6 mb-6">
          <h3 className="text-md font-semibold text-gray-800 mb-3">CTAボタン</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField id="ctaText" name="ctaText" label="ボタンテキスト" value={formData.hero?.ctaText} onChange={e => handleSimpleChange(e, 'hero')} placeholder="無料で始める" />
            <SelectField
              id="ctaAlign"
              name="ctaAlign"
              label="配置（位置）"
              value={formData.hero?.ctaAlign || 'center'}
              onChange={e => handleSimpleChange(e, 'hero')}
              options={alignOptions}
            />
            <ColorPickerInput
              id="ctaTextColor"
              name="ctaTextColor"
              label="文字色"
              value={formData.hero?.ctaTextColor}
              onChange={e => handleSimpleChange(e, 'hero')}
              defaultValue="#2563eb"
            />
          </div>
        </div>

        {/* レイアウト & オーバーレイ設定 */}
        <div className="border-b pb-6 mb-6">
          <h3 className="text-md font-semibold text-gray-800 mb-3">レイアウト & オーバーレイ設定</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <InputField
                id="overlayOpacity"
                name="overlayOpacity"
                label="黒色オーバーレイ濃淡 (0〜100%)"
                type="number"
                min="0"
                max="100"
                value={formData.hero?.overlayOpacity !== undefined ? formData.hero?.overlayOpacity : '50'}
                onChange={e => handleSimpleChange(e, 'hero')}
                placeholder="50"
              />
              <p className="text-xs text-gray-500 mt-1">※0%で完全透明、100%で完全に真っ黒になります。（デフォルト: 50%）</p>
            </div>
            <div>
              <InputField
                id="pcHeight"
                name="pcHeight"
                label="PC用縦幅 (px)"
                type="number"
                min="200"
                max="1200"
                value={formData.hero?.pcHeight || ''}
                onChange={e => handleSimpleChange(e, 'hero')}
                placeholder="例: 500（未設定時は自動調整）"
              />
              <p className="text-xs text-gray-500 mt-1">※PC表示時の最小縦幅をピクセル(px)で指定します。（未入力の場合はコンテンツに応じた高さ）</p>
            </div>
          </div>
        </div>

        {/* 背景画像 */}
        <div>
          <h3 className="text-md font-semibold text-gray-800 mb-3">背景画像</h3>
          <div className="flex items-center gap-4">
            <input type="file" id="heroImage" accept="image/*" onChange={e => handleImageUpload(e, 'hero', 'backgroundImage')} className="hidden" />
            <label htmlFor="heroImage" className="cursor-pointer bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center">
                <Upload size={16} className="mr-2"/>
                画像を選択
            </label>
            {uploading && <p>アップロード中...</p>}
            {formData.hero?.backgroundImage && (
              <div className="mt-2 flex items-center gap-4">
                  <img src={formData.hero.backgroundImage} alt="Preview" className="h-20 w-auto rounded" />
                  <button onClick={() => setFormData(prev => ({...prev, hero: {...prev.hero, backgroundImage: ''}}))} className="text-red-500 hover:text-red-700">
                      画像を削除
                  </button>
              </div>
            )}
          </div>
        </div>
      </SectionEditor>

      <SectionEditor title="特徴セクション">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <DynamicListEditor
            title="課題"
            items={formData.features?.problems}
            onUpdate={(e, index) => handleDynamicListChange(e, 'features', 'problems', index)}
            onAdd={() => addDynamicListItem('features', 'problems', true)}
            onRemove={(index) => removeDynamicListItem('features', 'problems', index)}
            showIconField={true}
            titleLabel="課題のタイトル"
            descriptionLabel="課題の説明"
          />
          <DynamicListEditor
            title="主な機能"
            items={formData.features?.solutions}
            onUpdate={(e, index) => handleDynamicListChange(e, 'features', 'solutions', index)}
            onAdd={() => addDynamicListItem('features', 'solutions', true)}
            onRemove={(index) => removeDynamicListItem('features', 'solutions', index)}
            showIconField={true}
            titleLabel="機能名"
            descriptionLabel="機能の説明"
          />
        </div>
      </SectionEditor>

      <SectionEditor title="ご利用の流れセクション">
        <DynamicListEditor
            title="ステップ"
            items={formData.flow?.steps}
            onUpdate={(e, index) => handleDynamicListChange(e, 'flow', 'steps', index)}
            onAdd={() => addDynamicListItem('flow', 'steps')}
            onRemove={(index) => removeDynamicListItem('flow', 'steps', index)}
        />
      </SectionEditor>

      <SectionEditor title="よくある質問セクション">
        <DynamicListEditor
            title="FAQ項目"
            items={formData.faq?.items}
            onUpdate={(e, index) => handleDynamicListChange(e, 'faq', 'items', index)}
            onAdd={() => addDynamicListItem('faq', 'items')}
            onRemove={(index) => removeDynamicListItem('faq', 'items', index)}
            titleLabel="質問"
            descriptionLabel="回答"
        />
      </SectionEditor>

      <SectionEditor title="CTA(Call To Action)セクション">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField id="title" name="title" label="CTAタイトル" value={formData.cta?.title} onChange={e => handleSimpleChange(e, 'cta')} />
          <InputField id="buttonText" name="buttonText" label="ボタンテキスト" value={formData.cta?.buttonText} onChange={e => handleSimpleChange(e, 'cta')} />
          <InputField id="link" name="link" label="ボタンリンク先URL" value={formData.cta?.link} onChange={e => handleSimpleChange(e, 'cta')} />
        </div>
      </SectionEditor>
    </div>
  );
};

export default LpManagement;
